import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Database, Package, Play, Check, X } from "lucide-react";
import {
  mongoBackup,
  mongoListCollections,
  mongoListDatabases,
  mongoProbe,
  mongoRestore,
  mongoSampleCollection,
} from "../../redux/actionCreator";

/**
 * MongoDB management for a project's server. One SSH probe checks that
 * mongosh / mongodump / mongorestore are installed, then four tabs cover
 * the everyday admin tasks:
 *
 *   Browse  — pick a db → list collections + counts → click one → first
 *             N documents rendered as JSON (read-only). Lets the user
 *             confirm models/sample data without leaving the portal.
 *   Backup  — mongodump on the server → SFTP back → browser save dialog.
 *             Optional db filter so you can dump a single database.
 *   Restore — upload an archive → SFTP to server → mongorestore. `--drop`
 *             is opt-in with an explicit confirm step.
 *   Probe   — diagnostics: which tools/version + connection state.
 *
 * The connection URI is set once on the server record (Servers → Edit →
 * Mongo URI) and stays encrypted at rest; this panel never displays it.
 */
export default function MongoPanel({ show, onHide, project, server, app, environment }) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState("browse");
  const projectId = project?._id;
  const serverId = server?._id;
  // App scope: when the panel is opened from an app card, forward the
  // appId + environment so the URI resolver picks the app's env vars
  // (MONGODB_URI etc.) before falling back to server.encrypted_mongo_uri.
  // Memoised so the child components' deps don't re-fire each render.
  const scope = useMemo(() => ({
    appId: app?._id || null,
    environment: environment || null,
  }), [app?._id, environment]);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);

  const reloadProbe = useCallback(async () => {
    if (!projectId || !serverId) return;
    setProbing(true);
    const res = await dispatch(mongoProbe(projectId, serverId, scope));
    setProbing(false);
    if (res?.status) setProbe(res.data);
  }, [dispatch, projectId, serverId, scope]);

  useEffect(() => { if (show) reloadProbe(); }, [show, reloadProbe]);

  const noUri = probe && !probe.has_uri;
  const noTools = probe && !probe.mongosh;
  const sourceLabel = probe?.uri_source || "";

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0">
          <span className="d-inline-flex align-items-center gap-2">
            <Database size={18} />
            MongoDB — {app?.name ? `${app.name} (${server?.label || "server"})` : (server?.label || "server")}
          </span>
        </h5>
      </Modal.Header>
      <Modal.Body>
        {sourceLabel && !noUri && (
          <div className="small text-muted mb-2">
            URI source: <code>{sourceLabel}</code>
          </div>
        )}
        {probing && !probe ? (
          <div className="text-center py-4"><Spinner animation="border" /> Probing…</div>
        ) : noUri ? (
          <div className="alert alert-warning">
            <strong>No Mongo URI configured.</strong>{" "}
            {app
              ? <>Set <code>MONGODB_URI</code> on this app's env vars, or configure "Mongo URI" on server "{server?.label}".</>
              : <>Set it on the server record (Project → Servers → Edit → "Mongo URI") so the portal can connect. URI is AES-GCM encrypted at rest.</>}
          </div>
        ) : noTools ? (
          <div className="alert alert-warning">
            <strong>mongosh / mongodump / mongorestore not found on PATH.</strong>{" "}
            Install them on this server:
            <pre className="small mb-0 mt-2">{`# Debian / Ubuntu
curl -fsSL https://www.mongodb.org/static/pgp/server-7.0.asc | sudo gpg -o /usr/share/keyrings/mongodb.gpg --dearmor
echo "deb [signed-by=/usr/share/keyrings/mongodb.gpg] https://repo.mongodb.org/apt/ubuntu \\
  $(lsb_release -cs)/mongodb-org/7.0 multiverse" | sudo tee /etc/apt/sources.list.d/mongodb.list
sudo apt update && sudo apt install -y mongodb-mongosh mongodb-database-tools`}</pre>
          </div>
        ) : (
          <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
            <Tab eventKey="browse" title="Browse">
              <BrowseTab projectId={projectId} serverId={serverId} scope={scope} />
            </Tab>
            <Tab eventKey="backup" title="Backup">
              <BackupTab projectId={projectId} serverId={serverId} server={server} scope={scope} />
            </Tab>
            <Tab eventKey="restore" title="Restore">
              <RestoreTab projectId={projectId} serverId={serverId} scope={scope} />
            </Tab>
            <Tab eventKey="probe" title="Tools">
              <ProbeTab probe={probe} onReload={reloadProbe} probing={probing} />
            </Tab>
          </Tabs>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={reloadProbe} disabled={probing}>↻ Re-probe</Button>
        <Button variant="primary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ───────────────── Browse: db → collections → sample docs ───────────── */

function BrowseTab({ projectId, serverId, scope = {} }) {
  const dispatch = useDispatch();
  const [dbs, setDbs] = useState([]);
  const [selectedDb, setSelectedDb] = useState("");
  const [collections, setCollections] = useState([]);
  const [selectedColl, setSelectedColl] = useState("");
  const [docs, setDocs] = useState(null);
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const loadDbs = useCallback(async () => {
    setLoading(true); setErr(null);
    const res = await dispatch(mongoListDatabases(projectId, serverId, scope));
    setLoading(false);
    if (res?.status) setDbs(res.data?.databases || []);
    else setErr(res?.message || "Failed to list databases");
  }, [dispatch, projectId, serverId, scope]);

  useEffect(() => { loadDbs(); }, [loadDbs]);

  const loadCollections = useCallback(async (db) => {
    setSelectedDb(db); setSelectedColl(""); setDocs(null); setCollections([]);
    if (!db) return;
    setLoading(true); setErr(null);
    const res = await dispatch(mongoListCollections(projectId, serverId, db, scope));
    setLoading(false);
    if (res?.status) setCollections(res.data?.collections || []);
    else setErr(res?.message || "Failed to list collections");
  }, [dispatch, projectId, serverId, scope]);

  const loadSample = useCallback(async (coll) => {
    setSelectedColl(coll); setDocs(null);
    if (!coll) return;
    setLoading(true); setErr(null);
    const res = await dispatch(mongoSampleCollection(projectId, serverId, selectedDb, coll, limit, scope));
    setLoading(false);
    if (res?.status) setDocs(res.data?.docs || []);
    else setErr(res?.message || "Failed to read sample");
  }, [dispatch, projectId, serverId, selectedDb, limit, scope]);

  const fmtSize = (b) => {
    if (!b) return "—";
    const u = ["B", "KB", "MB", "GB"]; let i = 0, v = b;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 && i ? 1 : 0)} ${u[i]}`;
  };

  return (
    <div className="row g-3">
      <div className="col-md-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <strong className="small">Databases</strong>
          <Button size="sm" variant="link" className="p-0" onClick={loadDbs}>↻</Button>
        </div>
        <div className="border rounded" style={{ maxHeight: 360, overflowY: "auto" }}>
          {loading && !dbs.length ? <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
            : dbs.length === 0 ? <div className="text-muted small p-2">No databases.</div>
            : dbs.map((d) => (
              <button
                key={d.name} type="button"
                className={`d-flex justify-content-between w-100 border-0 text-start px-2 py-1 ${selectedDb === d.name ? "bg-light fw-bold" : "bg-white"}`}
                onClick={() => loadCollections(d.name)}
              >
                <span><code className="small">{d.name}</code></span>
                <span className="small text-muted">{fmtSize(d.sizeOnDisk)}</span>
              </button>
            ))}
        </div>
      </div>
      <div className="col-md-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <strong className="small">Collections</strong>
          {selectedDb && <code className="small text-muted">{selectedDb}</code>}
        </div>
        <div className="border rounded" style={{ maxHeight: 360, overflowY: "auto" }}>
          {!selectedDb ? <div className="text-muted small p-2">Pick a database.</div>
            : collections.length === 0 ? <div className="text-muted small p-2">No collections.</div>
            : collections.map((c) => (
              <button
                key={c.name} type="button"
                className={`d-flex justify-content-between w-100 border-0 text-start px-2 py-1 ${selectedColl === c.name ? "bg-light fw-bold" : "bg-white"}`}
                onClick={() => loadSample(c.name)}
              >
                <span><code className="small">{c.name}</code></span>
                <span className="small text-muted">{c.count ?? 0} docs</span>
              </button>
            ))}
        </div>
      </div>
      <div className="col-md-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <strong className="small">Sample documents</strong>
          <Form.Select size="sm" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); if (selectedColl) loadSample(selectedColl); }} style={{ width: 80 }}>
            {[1, 5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </Form.Select>
        </div>
        <div className="border rounded" style={{ maxHeight: 360, overflowY: "auto", background: "#0b1220", color: "#e2e8f0" }}>
          {err ? <pre className="small p-2 mb-0 text-warning">{err}</pre>
            : !selectedColl ? <div className="text-muted small p-2">Pick a collection.</div>
            : !docs ? <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
            : <pre className="small p-2 mb-0" style={{ fontSize: 11 }}>{JSON.stringify(docs, null, 2)}</pre>}
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Backup tab ───────────────── */

function BackupTab({ projectId, serverId, scope = {} }) {
  const dispatch = useDispatch();
  const [db, setDb] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // tar.gz by default: it opens with ordinary tools (which is what people
  // expect from a "download backup" button) AND `tar` is preinstalled on
  // essentially every Linux server, whereas `zip` frequently is not. zip and
  // the exact-fidelity mongodump archive both stay available.
  const [format, setFormat] = useState("tar");

  const run = async () => {
    setBusy(true); setResult(null);
    const res = await dispatch(mongoBackup(projectId, serverId, db.trim() || null, { ...scope, format }));
    setBusy(false);
    setResult(res);
  };

  return (
    <div>
      <p className="small text-muted">
        Runs <code>mongodump --archive --gzip</code> on the server and streams the
        resulting archive back to your browser. The tmp file on the server is
        removed once the download finishes.
      </p>
      <Form.Label className="small mb-1">Format</Form.Label>
      <div className="d-flex gap-3 align-items-center mb-2 flex-wrap">
        <Form.Check
          type="radio" id="mongo-fmt-zip" label=".zip"
          checked={format === "zip"} disabled={busy}
          onChange={() => setFormat("zip")}
        />
        <Form.Check
          type="radio" id="mongo-fmt-tar" label=".tar.gz"
          checked={format === "tar"} disabled={busy}
          onChange={() => setFormat("tar")}
        />
        <Form.Check
          type="radio" id="mongo-fmt-archive" label=".archive.gz (mongodump)"
          checked={format === "archive"} disabled={busy}
          onChange={() => setFormat("archive")}
        />
      </div>
      {format === "archive" ? (
        <div className="alert alert-warning small py-2">
          <strong>Not a zip or tar.</strong> This is a single mongodump archive, so
          {" "}<code>unzip</code>, <code>tar</code> and macOS Archive Utility cannot
          open it — expected, not a corrupt download. Smallest and exactly
          faithful; restore via the <strong>Restore</strong> tab or:
          <pre className="mb-0 mt-2" style={{ fontSize: 11 }}>
{`mongorestore --uri="<uri>" --archive=<file>.archive.gz --gzip`}
          </pre>
        </div>
      ) : (
        <div className="alert alert-info small py-2">
          Runs <code>mongodump --out</code> and packs the result with
          {" "}<code>{format === "zip" ? "zip" : "tar -czf"}</code>, so the file opens with
          ordinary tools and you can see <code>&lt;db&gt;/&lt;collection&gt;.bson</code>
          {" "}plus <code>.metadata.json</code> inside. The <strong>Restore</strong> tab
          takes it back as-is.
          <div className="mt-1">
            Needs <code>{format === "zip" ? "zip" : "tar"}</code> on the server
            {format === "zip"
              ? " — tar is preinstalled on nearly every Linux box, zip often is not."
              : " — tar is preinstalled on nearly every Linux box."}
          </div>
          <div className="mt-1">
            The <code>.bson</code> files inside are binary. For readable JSON:
            {" "}<code>bsondump &lt;file&gt;.bson</code>.
          </div>
        </div>
      )}
      <div className="d-flex gap-2 align-items-end mb-3">
        <div style={{ flex: 1, maxWidth: 360 }}>
          <Form.Label className="small mb-1">Database (optional)</Form.Label>
          <Form.Control
            value={db}
            onChange={(e) => setDb(e.target.value)}
            placeholder="leave empty for ALL databases"
            disabled={busy}
            style={{ fontFamily: "ui-monospace, monospace" }}
          />
        </div>
        <Button variant="primary" onClick={run} disabled={busy}>
          {busy ? <Spinner size="sm" animation="border" /> : <span className="d-inline-flex align-items-center gap-1"><Package size={14} /> Backup → download</span>}
        </Button>
      </div>
      {result && (
        <div className={`alert ${result.status ? "alert-success" : "alert-danger"}`}>
          {result.message}
        </div>
      )}
    </div>
  );
}

/* ───────────────── Restore tab ───────────────── */

function RestoreTab({ projectId, serverId, scope = {} }) {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [drop, setDrop] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!file) return;
    if (drop && !window.confirm(
      `--drop is ENABLED. Existing collections in the target database(s) will be ` +
      `DELETED before the restore inserts. This cannot be undone. Continue?`,
    )) return;
    setBusy(true); setProgress(0); setResult(null);
    const res = await dispatch(mongoRestore(projectId, serverId, file, { ...scope, drop, onProgress: setProgress }));
    setBusy(false);
    setResult(res);
  };

  return (
    <div>
      <p className="small text-muted">
        Uploads a backup and runs <code>mongorestore</code> on the server. Takes
        any format the Backup tab produces — <code>.zip</code> or
        {" "}<code>.tar.gz</code> (unpacked on the server, then restored with
        {" "}<code>--dir</code>), or <code>.archive.gz</code> (restored with
        {" "}<code>--archive --gzip</code>). The format is detected from the file
        extension.{" "}
        <code>--drop</code> is opt-in for safety.
      </p>
      <Form.Group className="mb-3">
        <Form.Label>Backup file *</Form.Label>
        <Form.Control
          type="file"
          accept=".zip,.tar,.tar.gz,.tgz,.archive,.gz,application/zip,application/gzip,application/x-tar,application/octet-stream"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          disabled={busy}
        />
        {file && (
          <small className="text-muted">
            {file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB
          </small>
        )}
      </Form.Group>
      <Form.Check
        type="checkbox" id="restore-drop"
        checked={drop}
        onChange={(e) => setDrop(e.target.checked)}
        disabled={busy}
        label={
          <span>
            <strong className="text-danger">--drop</strong>{" "}
            <small className="text-muted">— delete every target collection before insert. Faster + clean, but destructive.</small>
          </span>
        }
        className="mb-3"
      />
      {busy && (
        <div className="mb-3">
          <div className="progress" style={{ height: 14 }}>
            <div className="progress-bar" style={{ width: `${progress}%` }}>{progress}%</div>
          </div>
          <small className="text-muted">
            {progress < 100 ? "Uploading…" : "mongorestore running on the server…"}
          </small>
        </div>
      )}
      <Button variant="primary" onClick={run} disabled={!file || busy}>
        {busy ? <Spinner size="sm" animation="border" /> : <span className="d-inline-flex align-items-center gap-1"><Play size={14} /> Restore</span>}
      </Button>
      {result && (
        <div className={`alert ${result.status ? "alert-success" : "alert-danger"} mt-3`}>
          <strong className="d-inline-flex align-items-center gap-2">
            {result.status ? <Check size={14} /> : <X size={14} />} {result.message}
          </strong>
          {result.data?.tail && (
            <pre className="small mt-2 mb-0" style={{ maxHeight: 200, overflowY: "auto" }}>
              {result.data.tail}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────── Probe tab ───────────────── */

function ProbeTab({ probe, onReload, probing }) {
  if (!probe) return <div className="text-center py-3"><Spinner animation="border" /></div>;
  const row = (label, value, hint) => (
    <div className="d-flex justify-content-between border-bottom py-2">
      <span className="small">{label}</span>
      <code className="small text-end">
        {value || "—"}
        {hint && <div className="small text-muted">{hint}</div>}
      </code>
    </div>
  );
  return (
    <div>
      {row("mongosh", probe.mongosh, probe.mongosh_version)}
      {row("mongodump", probe.mongodump)}
      {row("mongorestore", probe.mongorestore)}
      {row("URI configured?", probe.has_uri ? "yes" : "no")}
      {row("Connected?", probe.connected ? "yes" : (probe.has_uri ? "no" : "—"))}
      {probe.error && <div className="alert alert-warning small mt-2 mb-0">{probe.error}</div>}
      <div className="mt-3">
        <Button size="sm" variant="light" onClick={onReload} disabled={probing}>
          {probing ? <Spinner size="sm" animation="border" /> : "↻ Re-probe"}
        </Button>
      </div>
    </div>
  );
}
